import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Languages, Check, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeaders() {
  const token = localStorage.getItem('emina_local_token') || '';
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function AdminTranslationProvider() {
  const [cfg, setCfg] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  async function load() {
    try {
      const r = await fetch(`${API}/admin/translation-provider`, { headers: authHeaders() });
      if (!r.ok) throw new Error('load failed');
      const d = await r.json();
      setCfg(d.config || {});
      setDefaults(d.defaults || {});
    } catch (e) { toast.error('Greška pri učitavanju postavki'); }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    if (!cfg) return;
    setSaving(true);
    try {
      const body = { ...cfg };
      // Don't send the masked key back
      if ((body.libre_api_key || '').startsWith('****')) delete body.libre_api_key;
      const r = await fetch(`${API}/admin/translation-provider`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error('save failed');
      const d = await r.json();
      setCfg(d.config || {});
      toast.success('Spremljeno');
    } catch (e) { toast.error('Greška pri spremanju'); }
    finally { setSaving(false); }
  }

  async function test() {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await fetch(`${API}/admin/translation-provider/test`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ text: 'Pozdrav svijetu iz Međimurja', target: 'en' }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'test failed');
      setTestResult({ ok: true, ...d });
    } catch (e) { setTestResult({ ok: false, error: e.message }); }
    finally { setTesting(false); }
  }

  if (!cfg) return <div className="p-6 text-gray-500">Učitavanje...</div>;

  return (
    <div className="p-6 max-w-3xl" data-testid="admin-translation-provider">
      <div className="flex items-center gap-2 mb-6">
        <Languages size={22} className="text-red-600" />
        <h1 className="text-2xl font-bold">Prijevodi — provider</h1>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
        Odaberite uslugu prevođenja koju koristi auto-translator. Rezultati se keširaju,
        pa promjena ne uzrokuje ponovno prevođenje već prevedenih stringova.
      </p>

      {/* Provider selection */}
      <section className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl p-5 mb-4">
        <label className="block text-sm font-semibold mb-2">Aktivni provider</label>
        <div className="grid sm:grid-cols-3 gap-2">
          {[
            { k: 'gemini', t: 'Gemini (Emergent LLM Key)', hint: 'Preporučeno — koristi Emergent Universal Key' },
            { k: 'libre', t: 'LibreTranslate', hint: 'Self-hosted / besplatno' },
            { k: 'google', t: 'Google Translate', hint: 'Zahtijeva GOOGLE_TRANSLATE_API_KEY' },
          ].map(opt => (
            <button key={opt.k} onClick={() => setCfg({ ...cfg, provider: opt.k })}
              data-testid={`provider-${opt.k}`}
              className={`text-left p-4 rounded-lg border transition ${cfg.provider === opt.k ? 'border-red-600 bg-red-50 dark:bg-red-900/10' : 'border-gray-200 dark:border-slate-700 hover:border-red-300'}`}>
              <div className="flex items-center gap-2 font-semibold text-sm">
                {cfg.provider === opt.k && <Check size={14} className="text-red-600" />}
                {opt.t}
              </div>
              <div className="text-xs text-gray-500 mt-1">{opt.hint}</div>
            </button>
          ))}
        </div>
      </section>

      {cfg.provider === 'gemini' && (
        <section className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl p-5 mb-4">
          <label className="block text-sm font-semibold mb-2">Gemini model</label>
          <select value={cfg.gemini_model || defaults.gemini_model || 'gemini-2.5-flash'}
            onChange={e => setCfg({ ...cfg, gemini_model: e.target.value })}
            data-testid="gemini-model"
            className="w-full border border-gray-300 dark:border-slate-700 rounded-lg px-3 py-2 bg-white dark:bg-slate-950 text-sm">
            <option value="gemini-2.5-flash">gemini-2.5-flash (preporučeno — brz + jeftin)</option>
            <option value="gemini-2.5-pro">gemini-2.5-pro (najbolji, skuplji)</option>
            <option value="gemini-2.5-flash-lite">gemini-2.5-flash-lite (najjeftiniji)</option>
            <option value="gemini-3-flash-preview">gemini-3-flash-preview</option>
          </select>
        </section>
      )}

      {cfg.provider === 'libre' && (
        <section className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl p-5 mb-4 space-y-3">
          <div>
            <label className="block text-sm font-semibold mb-2">LibreTranslate URL</label>
            <input type="text" value={cfg.libre_url || ''}
              onChange={e => setCfg({ ...cfg, libre_url: e.target.value })}
              data-testid="libre-url"
              placeholder={defaults.libre_url}
              className="w-full border border-gray-300 dark:border-slate-700 rounded-lg px-3 py-2 bg-white dark:bg-slate-950 text-sm font-mono" />
            <p className="text-xs text-gray-500 mt-1">
              Public instance: <code>{defaults.libre_url}</code>. Za production preporučamo self-host
              (npr. <code>https://translate.vašdomena.hr/translate</code>).
            </p>
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2">API ključ (opcionalno)</label>
            <input type="password" value={cfg.libre_api_key || ''}
              onChange={e => setCfg({ ...cfg, libre_api_key: e.target.value })}
              data-testid="libre-api-key"
              placeholder="Ostavite prazno za public instancu"
              className="w-full border border-gray-300 dark:border-slate-700 rounded-lg px-3 py-2 bg-white dark:bg-slate-950 text-sm" />
          </div>
        </section>
      )}

      {cfg.provider === 'google' && (
        <section className="bg-amber-50 dark:bg-amber-900/10 border border-amber-300 dark:border-amber-900/40 rounded-xl p-5 mb-4">
          <div className="flex items-start gap-2 text-sm text-amber-800 dark:text-amber-300">
            <AlertCircle size={18} />
            <div>
              Google Translate koristi <code>GOOGLE_TRANSLATE_API_KEY</code> iz <code>backend/.env</code>.
              Ako nije postavljen, API pozivi će pasti i sustav će pasti na izvorni tekst.
            </div>
          </div>
        </section>
      )}

      <div className="flex items-center gap-3">
        <button onClick={save} disabled={saving} data-testid="save-translation-provider"
          className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg font-semibold disabled:opacity-50">
          {saving ? 'Spremam...' : 'Spremi'}
        </button>
        <button onClick={test} disabled={testing} data-testid="test-translation-provider"
          className="px-5 py-2.5 border border-gray-300 dark:border-slate-700 text-sm rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-slate-800 disabled:opacity-50">
          {testing ? 'Testiram...' : 'Test prijevoda'}
        </button>
      </div>

      {testResult && (
        <div className={`mt-4 p-4 rounded-lg border ${testResult.ok ? 'bg-emerald-50 border-emerald-300 dark:bg-emerald-900/10 dark:border-emerald-900/40' : 'bg-red-50 border-red-300 dark:bg-red-900/10 dark:border-red-900/40'}`}
             data-testid="test-result">
          {testResult.ok ? (
            <>
              <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300 mb-1">
                ✓ Test uspješan — provider: <code>{testResult.provider}</code>
              </p>
              <p className="text-xs"><strong>HR:</strong> {testResult.input}</p>
              <p className="text-xs"><strong>EN:</strong> {testResult.output}</p>
            </>
          ) : (
            <p className="text-sm text-red-700 dark:text-red-300">✗ {testResult.error}</p>
          )}
        </div>
      )}
    </div>
  );
}
